import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import TextTruncate from "components/TextTruncate";
import RadioInputGroup, { RadioInputViewType } from "components/Controls/RadioInputGroup";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import IconButton from "components/IconButton";
import TimingDialog from "../TimingDialog";
import { TimingFormFields } from "rs-components/TimingFields";
import ResultElementBaseSourceSelect from "../../../ResultElementBaseSourceSelect";
import { prepareSpaceViewerSourceDataForSelect, } from "../../../ResultElementBaseSourceSelect/utils";
import { parameterTypesSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { ResultElementSourceType } from "store/rs-workspace/rs-workspace.types";
import { incompleteSimulationsIdsSelector, OUTPUT_FINAL_ID, persistedInsightsDataSelector, workspaceInsightsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceOptimizationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { checkInsightHasInvalidScore } from "store/rs-workspace/utils/insights.utils";
import { INCOMPLETE_ENTITIES_MESSAGES } from "shared/labels";
import { getRecipeParameterUnitsItems, getSimulationTimestampOptions, getTimingSummaryText, } from "store/rs-workspace/utils/results.utils";
import { isSimulationVariantCompleted } from "store/rs-workspace/utils/completeness.utils";
import { SelectSourceFields, SourceFields } from "../../types";
import { findDictionaryItemByValue, recipeParameterUnitsType } from "shared/rsDictionary";
import { serverErrorsSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { getErrorByPropertyName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { getInsightSourceName, getRecipesCustomVariables, getSimulationSourceName, } from "store/rs-workspace/utils/scenario.utils";
import { isNotEmpty } from "utils/validate";
import DeleteIcon from "icons/delete.svg";
import * as styles from "../../styles.module.scss";
const SourceRow = ({ source, name, sources, restrictions = {}, upsert, remove }) => {
    const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
    const [timingDialogOpen, setTimingDialogOpen] = useState(false);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const variableOptionsList = useSelector(processVariablesOptionsSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const serverErrors = useSelector(serverErrorsSelector(RequestTemplate.SPACEVIEWER_ELEMENT_PUT));
    const incompleteSimulationIds = useSelector(incompleteSimulationsIdsSelector);
    const { Insight: insightRestrictions = {}, Simulation: simulationRestrictions = {}, ...sourceRestrictions } = useMemo(() => restrictions?.Source || {}, [restrictions]);
    const isSimulationSource = useMemo(() => source[SourceFields.type] === ResultElementSourceType.Simulation, [source]);
    const isInsightSource = useMemo(() => source[SourceFields.type] === ResultElementSourceType.Insight, [source]);
    const hasInvalidInsightScore = useMemo(() => {
        if (isSimulationSource || source.source === null) {
            return false;
        }
        return checkInsightHasInvalidScore(source[SourceFields.insightId], source[SourceFields.inputSetId], source[SourceFields.outputId], !!source[SourceFields.isFinal], persistedInsightsData);
    }, [isSimulationSource, persistedInsightsData, source]);
    const isSimulationIncomplete = useMemo(() => source.simulationId && incompleteSimulationIds.includes(source.simulationId), [incompleteSimulationIds, source.simulationId]);
    const sourceExternalError = useMemo(() => {
        if (isSimulationSource) {
            const simulation = simulations?.find(({ id }) => id === source.simulationId);
            const variant = simulation?.state?.adjustments?.find(({ id }) => id === source.adjustmentId);
            if (variant) {
                return !isSimulationVariantCompleted(simulation, cellModels, recipes, variant)
                    ? INCOMPLETE_ENTITIES_MESSAGES.SIMULATION_VARIANT_INCOMPLETE
                    : undefined;
            }
            else {
                return isSimulationIncomplete ? INCOMPLETE_ENTITIES_MESSAGES.SIMULATION_INCOMPLETE : undefined;
            }
        }
        if (isInsightSource && hasInvalidInsightScore) {
            return INCOMPLETE_ENTITIES_MESSAGES.INSIGHT_INCOMPLETE;
        }
        return undefined;
    }, [
        hasInvalidInsightScore,
        isInsightSource,
        isSimulationIncomplete,
        isSimulationSource,
        source.simulationId,
        source.adjustmentId,
        simulations,
        cellModels,
        recipes,
    ]);
    const timingDialogRecipeId = useMemo(() => {
        const isSimulation = source?.[SourceFields.type] === ResultElementSourceType.Simulation;
        if (!isSimulation) {
            return undefined;
        }
        return simulations.find(({ id }) => id === source[SourceFields.simulation_id])?.recipeId;
    }, [source, simulations]);
    const customVariablesList = useMemo(() => {
        const { customVariableId } = source;
        const usedRecipeId = simulations.find(({ id }) => id === source[SourceFields.simulation_id])?.recipeId;
        return getRecipesCustomVariables(recipes, [usedRecipeId], ({ recordInData, formula, id }) => isNotEmpty(customVariableId)
            ? Boolean(recordInData) && (!!formula?.complete || id === customVariableId?.value)
            : Boolean(recordInData) && !!formula?.complete);
    }, [recipes, source, simulations]);
    const customVariablesOptions = useMemo(() => {
        const { customVariableId } = source;
        const options = customVariablesList?.map(({ id, name, formula }) => {
            const unitHtml = parseComplexUnitToHtml(formula?.configuredUnits?.complexValue)?.html;
            return {
                value: id ?? 0,
                units: unitHtml,
                label: name ?? "",
                disabled: isNotEmpty(customVariableId) && id === customVariableId?.value && !formula?.complete,
            };
        });
        if (options.length !== 0) {
            return [
                {
                    value: "Custom Variables",
                    label: "Custom Variables",
                    isGroupName: true,
                    disabled: true,
                },
                ...options,
            ];
        }
        return [];
    }, [customVariablesList, source]);
    const variablesOptions = useMemo(() => {
        const sameAddedSimulations = sources.filter((item) => item.type === ResultElementSourceType.Simulation &&
            item?.simulationId === source[SourceFields.simulation_id]);
        const regularOptions = variableOptionsList.filter((option) => {
            const variableAlreadyUsed = sameAddedSimulations.length > 0 &&
                sameAddedSimulations.some((item) => item?.variableType === option.value);
            return !variableAlreadyUsed;
        });
        return [...regularOptions, ...customVariablesOptions];
    }, [sources, variableOptionsList, source, customVariablesOptions]);
    const timestampOptions = useMemo(() => getSimulationTimestampOptions(source[SourceFields.simulation_id], simulations, recipes), [recipes, simulations, source]);
    const timingSummaryText = useMemo(() => getTimingSummaryText(source[SourceFields.timing] || null, timestampOptions, processVariables, customVariablesList), [source, timestampOptions, processVariables, customVariablesList]);
    const unitsOptions = useMemo(() => {
        const selectedUnit = source[SourceFields.units]?.value;
        const options = getRecipeParameterUnitsItems("", selectedUnit, parameterTypes, [], true);
        return options.filter(({ value }) => value !== selectedUnit);
    }, [parameterTypes, source]);
    const selectItemRenderer = useCallback((item, { handleClick }) => (React.createElement(TextTruncate, { key: item.value, text: item?.label, className: classNames(styles.itemRenderer, { [styles.title]: item.isGroupName }, { [styles.disabled]: item.disabled }), onClick: !item.isGroupName ? handleClick : () => null })), []);
    const onSourceChange = useCallback((values) => {
        let sourcePayload = {};
        let sourceName = "";
        const conditions = [source[SourceFields.type] !== values.type];
        switch (values.type) {
            case ResultElementSourceType.Simulation: {
                const { simulationId, adjustmentId, optimizationId } = values;
                conditions.push(source[SourceFields.simulation_id] !== simulationId);
                sourceName = getSimulationSourceName(simulations, optimizations, simulationId, optimizationId, adjustmentId);
                sourcePayload = {
                    ...(conditions.some(Boolean)
                        ? {
                            ...source,
                            [SourceFields._variableName]: null,
                            [SourceFields.customVariableId]: null,
                            [SourceFields.variableType]: null,
                            [SourceFields.timing]: null,
                            [SourceFields.units]: null,
                        }
                        : source),
                    [SourceFields.simulation_id]: simulationId ?? 0,
                    [SourceFields.adjustmentId]: adjustmentId ?? null,
                };
                break;
            }
            case ResultElementSourceType.Insight: {
                const { insightId, inputSetId, output } = values;
                sourceName = getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes);
                sourcePayload = {
                    ...source,
                    [SourceFields.insightId]: insightId ?? 0,
                    [SourceFields.inputSetId]: inputSetId ?? 0,
                    [SourceFields.outputId]: output.id === OUTPUT_FINAL_ID ? undefined : (output.id ?? 0),
                    [SourceFields.isFinal]: output.id === OUTPUT_FINAL_ID,
                };
                break;
            }
        }
        upsert({
            ...source,
            ...sourcePayload,
            [SourceFields.source]: { value: sourceName, label: sourceName },
            [SourceFields.type]: values[SelectSourceFields.type] ?? ResultElementSourceType.Simulation,
        });
        setSourceDialogOpen(false);
    }, [insights, optimizations, persistedInsightsData, processVariables, recipes, simulations, source, upsert]);
    const onSimulationSourceUnitChange = useCallback((option) => {
        const defaultUnit = findDictionaryItemByValue(recipeParameterUnitsType, option.units);
        upsert({
            ...source,
            [SourceFields.units]: !source[SourceFields.units] ? defaultUnit : option,
        });
    }, [source, upsert]);
    const onSimulationSourceVariableChange = useCallback((option) => {
        const isCustomVariable = typeof option.value === "number";
        const defaultUnit = isCustomVariable
            ? { value: option.units, label: option.units }
            : findDictionaryItemByValue(recipeParameterUnitsType, option.units);
        const shouldSetDefaultUnit = !isCustomVariable &&
            (!source[SourceFields.units] || source[SourceFields.variableType]?.value !== option.value);
        const upsertValues = isCustomVariable
            ? {
                ...source,
                [SourceFields._variableName]: option,
                [SourceFields.variableType]: null,
                [SourceFields.customVariableId]: { ...option, value: Number(option.value) },
                [SourceFields.units]: { value: option.units, label: option.units },
            }
            : {
                ...source,
                [SourceFields._variableName]: option,
                [SourceFields.customVariableId]: null,
                [SourceFields.variableType]: { ...option, value: String(option.value) },
                ...(shouldSetDefaultUnit ? { [SourceFields.units]: defaultUnit } : {}),
            };
        upsert(upsertValues);
    }, [source, upsert]);
    const onViewChange = useCallback((view) => {
        const { id, ...restSource } = { ...source };
        upsert({
            id,
            ...restSource,
            [SourceFields.view]: view,
        });
    }, [source, upsert]);
    const onTimingFieldsChange = useCallback((timingValues) => {
        upsert({
            ...source,
            [SourceFields.timing]: timingValues,
        });
        setTimingDialogOpen(false);
    }, [source, upsert]);
    const deleteRow = useCallback(() => remove(source.id), [remove, source.id]);
    return (React.createElement("div", { className: styles.row },
        React.createElement("div", { className: classNames(styles.cell, styles.view) },
            React.createElement(Input, { name: `${name}.${SourceFields.view}`, component: RadioInputGroup, viewType: RadioInputViewType.RADIO, items: [{ value: true, label: "" }], classes: { container: styles.radio }, onChange: onViewChange })),
        React.createElement("div", { className: classNames(styles.cell, styles.source) },
            sourceDialogOpen && (React.createElement(ResultElementBaseSourceSelect, { title: "Data source", allowedTypes: [ResultElementSourceType.Simulation, ResultElementSourceType.Insight], initialValues: prepareSpaceViewerSourceDataForSelect(source), onSubmit: onSourceChange, onClose: () => setSourceDialogOpen(false), restrictions: {
                    Insight: insightRestrictions,
                    Simulation: simulationRestrictions,
                    Type: sourceRestrictions,
                }, classes: { target: styles.rowDialogTarget } })),
            React.createElement(Input, { name: `${name}.${SourceFields.source}`, component: SelectInput, items: [{}], placeholder: "Select", itemRenderer: SelectItemRenderer, restrictions: sourceRestrictions, classes: {
                    container: styles.selectWrapper,
                    component: styles.select,
                }, disableFormChange: true, popoverProps: { onInteraction: setSourceDialogOpen }, externalError: sourceExternalError })),
        React.createElement("div", { className: classNames(styles.cell, styles.variable) }, !isInsightSource && (React.createElement(Input, { name: `${name}.${SourceFields._variableName}`, component: SelectInput, items: variablesOptions, placeholder: "Select", disabled: !source[SourceFields.source], itemRenderer: selectItemRenderer, onChange: onSimulationSourceVariableChange, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: simulationRestrictions?.VariableType, externalError: getErrorByPropertyName(`Request.${name}.Source.VariableType`, serverErrors)?.text }))),
        React.createElement("div", { className: classNames(styles.cell, styles.unit) }, !isInsightSource && (React.createElement(Input, { name: `${name}.${SourceFields.units}`, component: SelectInput, items: unitsOptions, itemRenderer: SelectItemRenderer, disabled: !source[SourceFields.variableType]?.value, onChange: onSimulationSourceUnitChange, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: simulationRestrictions?.Units, placeholder: "Select", externalError: getErrorByPropertyName(`Request.${name}.Source.Units`, serverErrors)?.text }))),
        React.createElement("div", { className: classNames(styles.cell, styles.timing) }, !isInsightSource && (React.createElement("div", { className: styles["timing-container"] },
            timingDialogOpen && (React.createElement(TimingDialog, { name: "_timing", timestampOptions: timestampOptions, onSubmit: (values) => onTimingFieldsChange(values?._timing), onClose: () => setTimingDialogOpen(false), timing: source[SourceFields.timing] ?? {}, selectedVariable: source[SourceFields.variableType] ?? { label: "", value: "" }, restrictions: simulationRestrictions?.Timing, recipeId: timingDialogRecipeId })),
            React.createElement("div", null,
                React.createElement(Input, { name: `${name}.${SourceFields.timing}.${TimingFormFields.timestampType}`, component: SelectInput, itemRenderer: TimingDialog, placeholder: timingSummaryText || "Not specified", showTooltipIfDisabled: true, disabled: !source[SourceFields.variableType]?.value && !source[SourceFields.customVariableId]?.value, classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                    }, disableFormChange: true, popoverProps: { onInteraction: setTimingDialogOpen }, externalError: getErrorByPropertyName(`Request.${name}.Source.Timing`, serverErrors)?.text, showErrorIfUntouched: true, errorLabel: "Timestamp", restrictions: { IsNecessary: true } }))))),
        React.createElement("div", { className: classNames(styles.cell, styles.delete) },
            React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), title: "Delete", onClick: deleteRow }))));
};
export default SourceRow;
